import "@theme/bcs-video-player";

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const rootCleanups = new WeakMap();
const stickyBarSpaces = new Map();

document.documentElement.classList.add("bcs-landing-js");

const addCleanup = (root, cleanup) => {
  const cleanups = rootCleanups.get(root) || [];
  cleanups.push(cleanup);
  rootCleanups.set(root, cleanups);
};

const destroyRoot = (root) => {
  const cleanups = rootCleanups.get(root) || [];
  cleanups.forEach((cleanup) => cleanup());
  rootCleanups.delete(root);
  delete root.dataset.bcsLandingReady;
};

const getScrollSource = (element) => {
  let ancestor = element.parentElement;
  while (ancestor) {
    const overflowY = window.getComputedStyle(ancestor).overflowY;
    if (
      /(auto|scroll)/.test(overflowY) &&
      ancestor.scrollHeight > ancestor.clientHeight
    )
      return ancestor;
    ancestor = ancestor.parentElement;
  }
  return window;
};

const getViewportEdge = (source, fraction) => {
  if (source instanceof HTMLElement) {
    const rect = source.getBoundingClientRect();
    return rect.top + source.clientTop + source.clientHeight * fraction;
  }
  return window.innerHeight * fraction;
};

const getAnchorTarget = (href) => {
  if (typeof href !== "string" || !href.startsWith("#")) return null;
  try {
    return document.getElementById(decodeURIComponent(href.slice(1)));
  } catch {
    return null;
  }
};

const setupAnchorLinks = (root) => {
  if (!root.querySelector("[data-bcs-landing-anchor]")) return;
  const focusedTargets = new Map();
  const onClick = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !(event.target instanceof Element)
    )
      return;
    const link = event.target.closest("a[data-bcs-landing-anchor]");
    if (
      !(link instanceof HTMLAnchorElement) ||
      !root.contains(link) ||
      link.hasAttribute("download") ||
      (link.target && link.target !== "_self")
    )
      return;
    const href = link.getAttribute("href");
    if (!href?.startsWith("#")) return;
    event.preventDefault();
    const target = getAnchorTarget(href);
    if (!(target instanceof HTMLElement) || !target.getClientRects().length)
      return;

    if (!target.hasAttribute("tabindex")) {
      target.setAttribute("tabindex", "-1");
      const onBlur = () => {
        target.removeAttribute("tabindex");
        focusedTargets.delete(target);
      };
      focusedTargets.set(target, onBlur);
      target.addEventListener("blur", onBlur, { once: true });
    }
    target.focus({ preventScroll: true });
    const source = getScrollSource(target);
    const headerHeight =
      parseFloat(
        getComputedStyle(document.body).getPropertyValue("--header-height"),
      ) || 0;
    const margin = Math.max(
      headerHeight,
      parseFloat(getComputedStyle(target).scrollMarginTop) || 0,
    );
    const scrollTop =
      source instanceof HTMLElement ? source.scrollTop : window.scrollY;
    const top =
      scrollTop +
      target.getBoundingClientRect().top -
      getViewportEdge(source, 0) -
      margin;
    source.scrollTo({
      top: Math.max(0, top),
      behavior: reducedMotion.matches ? "instant" : "smooth",
    });
  };
  root.addEventListener("click", onClick);
  addCleanup(root, () => {
    root.removeEventListener("click", onClick);
    focusedTargets.forEach((onBlur, target) => {
      target.removeEventListener("blur", onBlur);
      target.removeAttribute("tabindex");
    });
    focusedTargets.clear();
  });
};

const setupStickyBar = (sticky, root) => {
  const link = sticky.querySelector(".bcs-landing-sticky-bar__button");
  if (!(link instanceof HTMLAnchorElement)) return;
  const spaceHost =
    sticky.closest("#MainContent") ||
    sticky.closest(".page-wrapper") ||
    document.body;
  let space = stickyBarSpaces.get(spaceHost);
  if (!space) {
    const element = document.createElement("div");
    element.className = "bcs-landing-sticky-bar-spacer";
    element.setAttribute("aria-hidden", "true");
    spaceHost.append(element);
    space = { element, heights: new Map(), height: 0 };
    stickyBarSpaces.set(spaceHost, space);
  }
  const updateSpace = () => {
    if (space.element !== spaceHost.lastElementChild) {
      spaceHost.append(space.element);
    }
    const height = Math.max(0, ...space.heights.values());
    if (height === space.height) return;
    space.height = height;
    space.element.style.setProperty(
      "--landing-sticky-bar-space",
      `${height}px`,
    );
  };
  let frame = 0;
  let observedFirstSection;
  let observedTarget;
  let observedBuyRow;
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };
  const observer = new ResizeObserver(schedule);
  observer.observe(sticky);
  observer.observe(document.body);
  const main = document.getElementById("MainContent");
  if (main) observer.observe(main);
  const update = () => {
    frame = 0;
    space.heights.set(sticky, sticky.offsetHeight);
    updateSpace();
    const href = link.getAttribute("href") || "";
    const isAnchor = href.startsWith("#");
    const target = getAnchorTarget(href);
    const firstSection =
      document.querySelector(
        "#MainContent > .shopify-section:not(.bcs-landing-sticky-bar-section)",
      ) || document.querySelector("[data-bcs-landing-hero]");
    const buyRow = target?.querySelector(".bcs-product-buy-row");
    if (
      firstSection !== observedFirstSection ||
      target !== observedTarget ||
      buyRow !== observedBuyRow
    ) {
      if (observedFirstSection) observer.unobserve(observedFirstSection);
      if (observedTarget) observer.unobserve(observedTarget);
      if (observedBuyRow) observer.unobserve(observedBuyRow);
      if (firstSection) observer.observe(firstSection);
      if (target) observer.observe(target);
      if (buyRow) observer.observe(buyRow);
      observedFirstSection = firstSection;
      observedTarget = target;
      observedBuyRow = buyRow;
    }
    let visible = false;
    let productVisible = false;
    if (
      href &&
      (!isAnchor ||
        (target instanceof HTMLElement && target.getClientRects().length))
    ) {
      const source = getScrollSource(target || firstSection || sticky);
      const viewportTop = getViewportEdge(source, 0);
      const viewportBottom = getViewportEdge(source, 1);
      const targetRect = target?.getBoundingClientRect();
      const purchaseEndRect =
        buyRow instanceof HTMLElement && buyRow.getClientRects().length
          ? buyRow.getBoundingClientRect()
          : targetRect;
      const scrollTop =
        source instanceof HTMLElement ? source.scrollTop : window.scrollY;
      const firstSectionRect = firstSection?.getBoundingClientRect();
      const revealReached =
        firstSection instanceof HTMLElement &&
        firstSection.getClientRects().length
          ? firstSectionRect.top + firstSectionRect.height / 2 <= viewportTop
          : scrollTop > 0;
      productVisible =
        !!targetRect &&
        purchaseEndRect.bottom > viewportTop &&
        targetRect.top < viewportBottom;
      visible = revealReached && !productVisible;
    }
    sticky.classList.toggle("is-visible", visible);
    sticky.classList.toggle("is-obscured", productVisible);
    sticky.inert = !visible;
  };
  document.addEventListener("scroll", schedule, {
    capture: true,
    passive: true,
  });
  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule);
  window.addEventListener("load", schedule);
  const editorEvents = [
    "shopify:section:load",
    "shopify:section:unload",
    "shopify:section:reorder",
  ];
  editorEvents.forEach((name) => document.addEventListener(name, schedule));
  sticky.hidden = false;
  update();
  addCleanup(root, () => {
    cancelAnimationFrame(frame);
    observer.disconnect();
    document.removeEventListener("scroll", schedule, true);
    window.removeEventListener("scroll", schedule);
    window.removeEventListener("resize", schedule);
    window.removeEventListener("load", schedule);
    editorEvents.forEach((name) =>
      document.removeEventListener(name, schedule),
    );
    sticky.hidden = true;
    sticky.inert = true;
    sticky.classList.remove("is-visible", "is-obscured");
    space.heights.delete(sticky);
    if (space.heights.size) {
      updateSpace();
    } else {
      space.element.remove();
      stickyBarSpaces.delete(spaceHost);
    }
  });
};

const setupProgressGroup = (group, root) => {
  if (reducedMotion.matches || typeof IntersectionObserver !== "function") {
    group.classList.add("is-visible");
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      group.classList.add("is-visible");
      observer.disconnect();
    },
    { threshold: 0.3 },
  );

  observer.observe(group);
  addCleanup(root, () => observer.disconnect());
};

const setupTimeline = (timeline, root) => {
  const milestones = [
    ...timeline.querySelectorAll("[data-bcs-timeline-milestone]"),
  ];
  const track = timeline.querySelector(".bcs-landing-timeline");
  if (!milestones.length || !(track instanceof HTMLElement)) return;

  const scrollSource = getScrollSource(timeline);
  let frame = 0;
  const update = () => {
    frame = 0;
    if (reducedMotion.matches) {
      timeline.style.setProperty("--timeline-progress", "1");
      milestones.forEach((milestone) => milestone.classList.add("is-active"));
      return;
    }

    const trackRect = track.getBoundingClientRect();
    const triggerY = getViewportEdge(scrollSource, 0.75);
    const progress = Math.max(
      0,
      Math.min(1, (triggerY - trackRect.top) / Math.max(1, trackRect.height)),
    );
    timeline.style.setProperty("--timeline-progress", String(progress));
    milestones.forEach((milestone, index) => {
      const threshold =
        milestones.length === 3
          ? [0.1, 0.5, 0.88][index]
          : (index + 0.5) / milestones.length;
      milestone.classList.toggle("is-active", progress >= threshold);
    });
  };

  const queueUpdate = () => {
    if (!frame) frame = window.requestAnimationFrame(update);
  };

  update();
  scrollSource.addEventListener("scroll", queueUpdate, { passive: true });
  window.addEventListener("resize", queueUpdate);
  reducedMotion.addEventListener("change", queueUpdate);
  addCleanup(root, () => {
    scrollSource.removeEventListener("scroll", queueUpdate);
    window.removeEventListener("resize", queueUpdate);
    reducedMotion.removeEventListener("change", queueUpdate);
    if (frame) window.cancelAnimationFrame(frame);
  });
};

const setupReviews = (reviews, root) => {
  const slider = reviews.querySelector("[data-bcs-reviews-swiper]");
  const slides = slider
    ? [...slider.querySelectorAll("[data-bcs-review-slide]")]
    : [];
  if (
    !(slider instanceof HTMLElement) ||
    slides.length < 2 ||
    typeof window.Swiper !== "function"
  )
    return;

  const swiper = new window.Swiper(slider, {
    slidesPerView: 1,
    slidesPerGroup: 1,
    rewind: true,
    effect: "fade",
    fadeEffect: {
      crossFade: true,
    },
    speed: reducedMotion.matches ? 0 : 240,
    allowTouchMove: true,
    grabCursor: true,
    autoHeight: true,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    navigation: {
      prevEl: reviews.querySelector("[data-bcs-reviews-prev]"),
      nextEl: reviews.querySelector("[data-bcs-reviews-next]"),
    },
    pagination: {
      el: reviews.querySelector("[data-bcs-reviews-pagination]"),
      clickable: true,
    },
  });

  reviews.bcsLandingSwiper = swiper;
  addCleanup(root, () => {
    if (!swiper.destroyed) swiper.destroy(true, true);
    delete reviews.bcsLandingSwiper;
  });
};

const setupStickyCta = (sticky, root) => {
  sticky.hidden = false;
  const wrapper = sticky.closest(".page-wrapper");
  let frame = 0;
  const updateVisibility = () => {
    frame = 0;
    const source = getScrollSource(sticky);
    if (sticky.dataset.stickyMode === "editorial") {
      const scrollHeight =
        source instanceof HTMLElement
          ? source.scrollHeight
          : Math.max(
              document.documentElement.scrollHeight,
              document.body.scrollHeight,
            );
      const scrollTop =
        source instanceof HTMLElement ? source.scrollTop : window.scrollY;
      const end = [
        ...document.querySelectorAll("[data-bcs-landing-end-zone]"),
      ].at(-1);
      const endRect = end?.getBoundingClientRect();
      const viewportHeight =
        source instanceof HTMLElement
          ? source.clientHeight
          : window.innerHeight;
      const remaining = scrollHeight - scrollTop - viewportHeight;
      const hide =
        (!!endRect && endRect.top < getViewportEdge(source, 0.99)) ||
        remaining <= Math.max(20, viewportHeight * 0.08);
      const percent = Math.min(
        50,
        Math.max(5, Number(sticky.dataset.stickyRevealPct) || 20),
      );
      const active = scrollTop >= Math.round((scrollHeight * percent) / 100);
      sticky.classList.toggle("is-visible", active);
      sticky.classList.toggle("is-obscured", hide);
      sticky.inert = !active || hide;
      return;
    }
    let trigger;
    let target;
    try {
      trigger = document.querySelector(sticky.dataset.trigger);
      target = document.querySelector(sticky.dataset.target);
    } catch {
      trigger = null;
      target = null;
    }
    const triggerReached =
      !(trigger instanceof HTMLElement) ||
      trigger.getBoundingClientRect().top <= getViewportEdge(source, 1.15);
    const targetReached =
      target instanceof HTMLElement &&
      target.getBoundingClientRect().top <= getViewportEdge(source, 1);
    sticky.classList.toggle("is-visible", triggerReached);
    sticky.classList.toggle("is-obscured", targetReached);
    sticky.inert = !triggerReached || targetReached;
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(updateVisibility);
  };
  window.addEventListener("scroll", schedule, { passive: true });
  wrapper?.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule);
  window.addEventListener("load", schedule);
  [
    "shopify:section:load",
    "shopify:section:unload",
    "shopify:section:reorder",
  ].forEach((name) => document.addEventListener(name, schedule));
  const observer = new ResizeObserver(schedule);
  observer.observe(wrapper || document.body);
  updateVisibility();
  addCleanup(root, () => {
    cancelAnimationFrame(frame);
    observer.disconnect();
    window.removeEventListener("scroll", schedule);
    wrapper?.removeEventListener("scroll", schedule);
    window.removeEventListener("resize", schedule);
    window.removeEventListener("load", schedule);
    [
      "shopify:section:load",
      "shopify:section:unload",
      "shopify:section:reorder",
    ].forEach((name) => document.removeEventListener(name, schedule));
  });
};

const setupCitations = (citations, root) => {
  const button = citations.querySelector("[data-bcs-landing-citations-toggle]");
  const panel = citations.querySelector("[data-bcs-landing-citations-panel]");
  if (!(button instanceof HTMLButtonElement) || !(panel instanceof HTMLElement))
    return;
  panel.hidden = true;
  button.setAttribute("aria-expanded", "false");
  citations.classList.add("is-enhanced");
  const toggle = () => {
    panel.hidden = !panel.hidden;
    button.setAttribute("aria-expanded", String(!panel.hidden));
  };
  button.addEventListener("click", toggle);
  addCleanup(root, () => {
    button.removeEventListener("click", toggle);
    panel.hidden = false;
    button.setAttribute("aria-expanded", "true");
    citations.classList.remove("is-enhanced");
  });
};

const setupVideos = (videos, root) => {
  const track = videos.querySelector("[data-bcs-landing-video-track]");
  if (!(track instanceof HTMLElement)) return;
  const cards = [...track.querySelectorAll("[data-bcs-landing-video-card]")];
  const pauseWithoutPoster = (video) => {
    if (!(video instanceof HTMLVideoElement) || video.paused) return;
    video.dataset.bcsSkipPosterPause = "true";
    video.pause();
  };
  const pauseAll = () =>
    cards.forEach((card) => pauseWithoutPoster(card.querySelector("video")));
  let swiper;
  const initCarousel = () => {
    if (swiper || typeof Swiper !== "function" || !cards.length) return;
    swiper = new Swiper(track, {
      slidesPerView: 1.7,
      spaceBetween: 14,
      grabCursor: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resistanceRatio: 0.85,
      speed: reducedMotion.matches ? 0 : 450,
      breakpoints: { 750: { slidesPerView: 3, spaceBetween: 24 } },
      pagination:
        cards.length > 1
          ? {
              el: videos.querySelector("[data-bcs-landing-video-dots]"),
              clickable: true,
              bulletElement: "button",
              bulletClass: "bcs-landing-videos__dot",
              bulletActiveClass: "bcs-landing-videos__dot--active",
            }
          : false,
      on: {
        slideChange: pauseAll,
        touchStart: (_swiper, event) => {
          if (!event?.target?.closest("video")) pauseAll();
        },
      },
    });
    videos.bcsLandingSwiper = swiper;
  };
  initCarousel();
  window.addEventListener("load", initCarousel, { once: true });
  cards.forEach((card) => {
    const video = card.querySelector("video");
    const button = card.querySelector("[data-bcs-landing-video-play]");
    if (
      !(video instanceof HTMLVideoElement) ||
      !(button instanceof HTMLButtonElement)
    )
      return;
    card.classList.add("is-enhanced");
    video.controls = false;
    let posterTimer;
    let focusVideoOnPlaying = false;
    const onPlay = () => {
      cards.forEach((other) => {
        if (other !== card) pauseWithoutPoster(other.querySelector("video"));
      });
    };
    const onPlaying = () => {
      clearTimeout(posterTimer);
      button.hidden = true;
      video.controls = true;
      if (focusVideoOnPlaying) video.focus({ preventScroll: true });
      focusVideoOnPlaying = false;
    };
    const onPause = () => {
      if (video.dataset.bcsSkipPosterPause === "true") {
        delete video.dataset.bcsSkipPosterPause;
        return;
      }
      clearTimeout(posterTimer);
      posterTimer = window.setTimeout(() => {
        if (video.paused) button.hidden = false;
      }, 280);
    };
    const onError = () => {
      clearTimeout(posterTimer);
      button.hidden = false;
    };
    const playVideo = async (event) => {
      focusVideoOnPlaying =
        event.detail === 0 && button.matches(":focus-visible");
      const cardIndex = cards.indexOf(card);
      if (swiper && !swiper.destroyed) {
        const sectionBounds = videos.getBoundingClientRect();
        const cardBounds = card.getBoundingClientRect();
        const hiddenOnRight = cardBounds.right - sectionBounds.right;
        const hiddenOnLeft = sectionBounds.left - cardBounds.left;
        if (cardIndex === cards.length - 1) {
          if (hiddenOnRight > 1) videos.scrollLeft += hiddenOnRight;
        } else if (
          videos.scrollLeft > 1 ||
          hiddenOnLeft > 1 ||
          hiddenOnRight > 1
        ) {
          swiper.slideTo(cardIndex);
          videos.scrollLeft = 0;
        }
      }
      video.preload = "auto";
      video.controls = true;
      try {
        await video.play();
      } catch {
        focusVideoOnPlaying = false;
        button.hidden = false;
      }
    };
    button.addEventListener("click", playVideo);
    video.addEventListener("play", onPlay);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onPause);
    video.addEventListener("error", onError);
    addCleanup(root, () => {
      clearTimeout(posterTimer);
      button.removeEventListener("click", playVideo);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onPause);
      video.removeEventListener("error", onError);
      video.pause();
      video.controls = true;
      delete video.dataset.bcsSkipPosterPause;
      button.hidden = false;
      card.classList.remove("is-enhanced");
    });
  });
  addCleanup(root, () => {
    window.removeEventListener("load", initCarousel);
    if (swiper && !swiper.destroyed) swiper.destroy(true, true);
    delete videos.bcsLandingSwiper;
  });
};

const initRoot = (root) => {
  if (!(root instanceof HTMLElement) || root.dataset.bcsLandingReady === "true")
    return;
  root.dataset.bcsLandingReady = "true";

  setupAnchorLinks(root);
  root
    .querySelectorAll("[data-bcs-sticky-bar]")
    .forEach((sticky) => setupStickyBar(sticky, root));

  root
    .querySelectorAll("[data-bcs-progress-group]")
    .forEach((group) => setupProgressGroup(group, root));
  root
    .querySelectorAll("[data-bcs-landing-timeline]")
    .forEach((timeline) => setupTimeline(timeline, root));
  root
    .querySelectorAll("[data-bcs-landing-reviews]")
    .forEach((reviews) => setupReviews(reviews, root));
  root
    .querySelectorAll("[data-bcs-sticky-cta]")
    .forEach((sticky) => setupStickyCta(sticky, root));
  root
    .querySelectorAll(".bcs-landing-step__citations")
    .forEach((citations) => setupCitations(citations, root));
  root
    .querySelectorAll("[data-bcs-landing-videos]")
    .forEach((videos) => setupVideos(videos, root));
};

const initAll = (scope = document) => {
  if (scope instanceof HTMLElement && scope.matches(".shopify-section")) {
    initRoot(scope);
    return;
  }
  scope.querySelectorAll(".shopify-section").forEach(initRoot);
};

initAll();
window.addEventListener("load", () => initAll(), { once: true });

document.addEventListener("shopify:section:load", (event) => {
  initRoot(event.target);
});

document.addEventListener("shopify:section:unload", (event) => {
  if (event.target instanceof HTMLElement) destroyRoot(event.target);
});

document.addEventListener("shopify:block:select", (event) => {
  const block = event.target;
  if (!(block instanceof HTMLElement)) return;
  const videoCard = block.closest("[data-bcs-landing-video-card]");
  if (videoCard instanceof HTMLElement) {
    const videos = videoCard.closest("[data-bcs-landing-videos]");
    const swiper = videos?.bcsLandingSwiper;
    if (swiper && !swiper.destroyed) {
      const cards = [
        ...videos.querySelectorAll("[data-bcs-landing-video-card]"),
      ];
      swiper.slideTo(cards.indexOf(videoCard), 0);
    }
    videoCard.scrollIntoView({
      behavior: "instant",
      block: "nearest",
      inline: "nearest",
    });
    videos?.querySelectorAll("video").forEach((video) => video.pause());
  }
  const slide = block.closest("[data-bcs-review-slide]");
  const reviews = block.closest("[data-bcs-landing-reviews]");
  if (!(slide instanceof HTMLElement) || !(reviews instanceof HTMLElement))
    return;
  const slides = [...reviews.querySelectorAll("[data-bcs-review-slide]")];
  const index = slides.indexOf(slide);
  if (
    index >= 0 &&
    reviews.bcsLandingSwiper &&
    !reviews.bcsLandingSwiper.destroyed
  ) {
    reviews.bcsLandingSwiper.slideTo(index, reducedMotion.matches ? 0 : 320);
  }
});
